import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../enviroment";
import {
  ClarityConsentState,
  CONSENT_CHANGED_EVENT,
  readConsent,
  writeConsent,
} from "../../../utils/analytics/clarityConsent";
import {
  activateAnalytics,
  clarityConsent,
  resendIdentity,
} from "../../../utils/analytics/clarity";
import { SecondaryButton } from "../Buttons/SecondaryButton";

// Flag REACT_APP_CLARITY_CONSENT_ENABLED - build-time constant
// (SupplierStatusBanner pattern): off = a null component, no hook, no
// storage read = byte-identical DOM. On = the consent banner at the bottom
// of AuthenticatedView (never on PublicView / /approval), rendered only
// while no valid choice is stored and only when the privacy policy URL is
// configured - consent without accessible information is invalid, so
// without the URL nothing is shown and no analytics is ever loaded.
const consentEnabled = enviroment.clarityConsentEnabled;

// Both buttons share this exact class list: same look, same weight, no
// default - Decline left, Accept right (EDPB/Datatilsynet: no nudging).
export const CONSENT_BUTTON_CLASS = "w-28 justify-center";
export const PRIVACY_URL_WARNING =
  "ConsentBanner: REACT_APP_PRIVACY_POLICY_URL missing";

let warnedMissingUrl = false;

export function __resetConsentBannerForTests(): void {
  warnedMissingUrl = false;
}

export function ConsentBanner(): JSX.Element | null {
  if (!consentEnabled) return null;
  return <ConsentBannerInner />;
}

function ConsentBannerInner(): JSX.Element | null {
  const { t } = useTranslation();
  const privacyPolicyUrl = enviroment.privacyPolicyUrl;
  const [state, setState] = useState<ClarityConsentState>(() =>
    readConsent(),
  );

  useEffect(() => {
    if (!privacyPolicyUrl && !warnedMissingUrl) {
      warnedMissingUrl = true;
      console.warn(PRIVACY_URL_WARNING);
    }
  }, [privacyPolicyUrl]);

  // A choice made in the Cookie settings dialog while the banner is still
  // up: writeConsent dispatches the event, the banner drops out.
  useEffect(() => {
    function onChanged() {
      setState(readConsent());
    }
    window.addEventListener(CONSENT_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChanged);
  }, []);

  if (!privacyPolicyUrl || state !== "unset") return null;

  function handleAccept() {
    writeConsent("granted");
    activateAnalytics();
    resendIdentity();
    setState("granted");
  }

  function handleDecline() {
    writeConsent("denied");
    clarityConsent(false);
    setState("denied");
  }

  return (
    <div
      role="region"
      aria-label={t("consent.title")}
      className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-300 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
    >
      <div className="max-w-[90rem] mx-auto px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 min-w-0 text-sm text-gray-800">
          <span className="font-semibold mr-1">{t("consent.title")}.</span>
          <span>{t("consent.body")}</span>{" "}
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="underline whitespace-nowrap"
          >
            {t("consent.privacyLink")}
          </a>
        </div>
        <div className="flex gap-2 shrink-0">
          <SecondaryButton
            type="button"
            width="sm"
            className={CONSENT_BUTTON_CLASS}
            onClick={handleDecline}
          >
            {t("consent.decline")}
          </SecondaryButton>
          <SecondaryButton
            type="button"
            width="sm"
            className={CONSENT_BUTTON_CLASS}
            onClick={handleAccept}
          >
            {t("consent.accept")}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
